import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, CalendarClock, Palette, Armchair, Images, LogOut, ExternalLink, Plus, ChevronDown, X, Check, Wallet, History, BarChart3 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GuestsTab from "@/components/dashboard/GuestsTab";
import ProgramTab from "@/components/dashboard/ProgramTab";
import SettingsTab from "@/components/dashboard/SettingsTab";
import TablesTab from "@/components/dashboard/TablesTab";
import PhotosTab from "@/components/dashboard/PhotosTab";
import BudgetTab from "@/components/dashboard/BudgetTab";
import ActivityTab from "@/components/dashboard/ActivityTab";
import StatsTab from "@/components/dashboard/StatsTab";

const TABS = [
  { id: "guests", label: "Invitati", icon: Users },
  { id: "tables", label: "Tavoli", icon: Armchair },
  { id: "program", label: "Programma", icon: CalendarClock },
  { id: "budget", label: "Budget & Organizzazione", icon: Wallet },
  { id: "photos", label: "Foto", icon: Images },
  { id: "settings", label: "Pagina & Tema", icon: Palette },
  { id: "stats", label: "Statistiche", icon: BarChart3 },
  { id: "activity", label: "Attività", icon: History },
];

const PRESETS = ["#BFA588", "#8F9779", "#2C3E35", "#A6544C", "#5B6B8C", "#B08BA5"];

// yyyy-MM-ddTHH:mm in the browser's local time, usable as a datetime-local `min`.
function nowLocalDatetime() {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function slugify(text) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "e")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function NewWeddingModal({ onClose, onCreated }) {
  const [nome, setNome] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [data, setData] = useState("");
  const [location, setLocation] = useState("");
  const [locationMapUrl, setLocationMapUrl] = useState("");
  const [primary, setPrimary] = useState("#BFA588");
  const minDatetime = nowLocalDatetime();
  const [saving, setSaving] = useState(false);
  // Fixed per modal session: appended to the auto-suggested slug so the public
  // URL isn't a guessable "nome-e-cognome" pattern (e.g. marco-e-giulia-7183).
  const [randomSuffix] = useState(() => String(Math.floor(1000 + Math.random() * 9000)));

  const onNome = (v) => {
    setNome(v);
    if (!slugEdited) setSlug(`${slugify(v)}-${randomSuffix}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!nome.trim()) return toast.error("Inserisci i nomi degli sposi");
    if (!slug.trim()) return toast.error("Inserisci uno slug");
    if (!data) return toast.error("Inserisci data e ora dell'evento");
    if (data < minDatetime) return toast.error("La data dell'evento non può essere nel passato");
    setSaving(true);
    try {
      const { data: wed } = await api.post("/owner/weddings", {
        slug,
        nome_sposi: nome,
        data_evento: data,
        location,
        location_map_url: locationMapUrl,
        tema_colore: { primary, font: "Playfair Display" },
      });
      toast.success("Matrimonio creato!");
      onCreated(wed);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nella creazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-wedding-modal">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[#faf9f6] rounded-3xl p-7 space-y-5 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="eyebrow mb-1">Nuovo evento</p>
            <h2 className="font-wed text-2xl">Crea un matrimonio</h2>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="new-wedding-close"><X size={20} /></button>
        </div>

        <div>
          <label className="label-wed">Nomi sposi *</label>
          <input className="input-wed" value={nome} onChange={(e) => onNome(e.target.value)} placeholder="Es. Marco & Giulia" data-testid="new-wedding-names" />
        </div>

        <div>
          <label className="label-wed">Slug (indirizzo pubblico) *</label>
          <div className="flex items-center gap-1 text-sm text-neutral-400">
            <span className="whitespace-nowrap">…/</span>
            <input className="input-wed !py-2" value={slug} onChange={(e) => { setSlug(slugify(e.target.value)); setSlugEdited(true); }} placeholder={`marco-e-giulia-${randomSuffix}`} data-testid="new-wedding-slug" />
          </div>
          <p className="text-xs text-neutral-400 mt-1">I numeri finali sono aggiunti automaticamente per rendere l'indirizzo non indovinabile. Puoi modificarlo liberamente.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-wed">Data e ora *</label>
            <input className="input-wed" type="datetime-local" min={minDatetime} value={data} onChange={(e) => setData(e.target.value)} data-testid="new-wedding-date" />
          </div>
          <div>
            <label className="label-wed">Location</label>
            <input className="input-wed" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Villa dei Cipressi" data-testid="new-wedding-location" />
          </div>
        </div>

        <div>
          <label className="label-wed">Link Google Maps (facoltativo)</label>
          <input className="input-wed" value={locationMapUrl} onChange={(e) => setLocationMapUrl(e.target.value)} placeholder="https://maps.app.goo.gl/…" data-testid="new-wedding-maps-link" />
          <p className="text-xs text-neutral-400 mt-1">Potrai aggiungerlo o modificarlo anche dopo, da Pagina & Tema.</p>
        </div>

        <div>
          <label className="label-wed">Colore tema</label>
          <div className="flex items-center gap-3 flex-wrap">
            {PRESETS.map((c) => (
              <button key={c} type="button" onClick={() => setPrimary(c)} className={`w-9 h-9 rounded-full border-2 transition-transform duration-150 hover:scale-110 ${primary === c ? "ring-2 ring-offset-2" : ""}`} style={{ background: c, borderColor: "#fff" }} data-testid={`new-wedding-color-${c.slice(1)}`} />
            ))}
            <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} className="w-9 h-9 rounded cursor-pointer border-0" />
          </div>
        </div>

        <button type="submit" className="btn-wed w-full" disabled={saving} data-testid="new-wedding-submit">
          {saving ? "Creazione…" : "Crea matrimonio"}
        </button>
      </form>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [weddings, setWeddings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [tab, setTab] = useState("guests");
  const [showNew, setShowNew] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  const loadWeddings = (selectId) => {
    return api.get("/owner/weddings").then((r) => {
      setWeddings(r.data);
      setLoaded(true);
      setActiveId((prev) => selectId || prev || (r.data[0]?.id ?? null));
    }).catch(() => setLoaded(true));
  };

  useEffect(() => {
    if (user) loadWeddings();
  }, [user]);

  useEffect(() => {
    if (!activeId) return;
    api.defaults.params = { ...api.defaults.params, wedding_id: activeId };
    setWedding(null);
    api.get("/owner/wedding").then((r) => setWedding(r.data)).catch(() => {});
  }, [activeId]);

  if (!user)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-wed text-xl" style={{ color: "var(--wed-primary)" }}>Caricamento…</div>
      </div>
    );

  const activeWedding = weddings.find((w) => w.id === activeId);
  const ActiveTab = { guests: GuestsTab, tables: TablesTab, program: ProgramTab, budget: BudgetTab, photos: PhotosTab, settings: SettingsTab, stats: StatsTab, activity: ActivityTab }[tab];

  return (
    <div className="min-h-screen bg-[#faf9f6]" data-testid="dashboard-page">
      <header id="dashboard-header" className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b" style={{ borderColor: "#eee8dd" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="shrink-0 hidden sm:block" data-testid="dashboard-brand-link">
            <img src="/brand/logo-black.png" alt="ourweddly" className="h-9 w-auto" />
          </Link>
          <div className="relative">
            <p className="text-[0.65rem] tracking-[0.25em] uppercase text-neutral-400">Dashboard sposi</p>
            <button
              onClick={() => setShowSwitcher((s) => !s)}
              className="flex items-center gap-2 group"
              data-testid="wedding-switcher-button"
            >
              <h1 className="font-wed text-xl" data-testid="dashboard-wedding-name">{activeWedding?.nome_sposi || "—"}</h1>
              {weddings.length > 0 && <ChevronDown size={16} className="text-neutral-400 group-hover:text-neutral-700 transition-colors" />}
            </button>
            {showSwitcher && (
              <div className="absolute left-0 mt-2 w-64 bg-white border rounded-2xl shadow-lg overflow-hidden z-50" style={{ borderColor: "#e3ddd2" }} data-testid="wedding-switcher-menu">
                {weddings.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => { setActiveId(w.id); setShowSwitcher(false); }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-neutral-50 flex items-center justify-between transition-colors duration-150"
                    data-testid={`wedding-option-${w.id}`}
                  >
                    <span className="truncate">{w.nome_sposi}</span>
                    {w.id === activeId && <Check size={15} style={{ color: "var(--wed-primary)" }} />}
                  </button>
                ))}
                <button
                  onClick={() => { setShowSwitcher(false); setShowNew(true); }}
                  className="w-full text-left px-4 py-3 text-sm border-t flex items-center gap-2 hover:bg-neutral-50 transition-colors duration-150"
                  style={{ borderColor: "#eee8dd", color: "var(--wed-primary)" }}
                  data-testid="wedding-switcher-new"
                >
                  <Plus size={15} /> Nuovo matrimonio
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowNew(true)} className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2" data-testid="dashboard-new-wedding-button">
              <Plus size={15} /> <span className="hidden sm:inline">Nuovo</span>
            </button>
            {activeWedding && (
              <a href={`/${activeWedding.slug}`} target="_blank" rel="noreferrer" className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2" data-testid="dashboard-view-public-link">
                <ExternalLink size={15} /> <span className="hidden sm:inline">Pagina pubblica</span>
              </a>
            )}
            <button onClick={async () => { await logout(); localStorage.removeItem("access_token"); navigate("/login"); }} className="text-neutral-400 hover:text-neutral-700 p-2 transition-colors duration-200" data-testid="dashboard-logout-button">
              <LogOut size={19} />
            </button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-4 sm:px-8 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors duration-200 ${tab === t.id ? "font-medium" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}
              style={tab === t.id ? { borderColor: "var(--wed-primary)", color: "var(--wed-primary)" } : {}}
              data-testid={`dashboard-tab-${t.id}`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        {loaded && weddings.length === 0 ? (
          <div className="text-center py-24" data-testid="dashboard-no-weddings">
            <p className="font-wed text-2xl mb-3">Nessun matrimonio ancora</p>
            <p className="text-neutral-500 mb-6">Crea il tuo primo evento in pochi click.</p>
            <button onClick={() => setShowNew(true)} className="btn-wed inline-flex items-center gap-2" data-testid="dashboard-create-first">
              <Plus size={16} /> Crea matrimonio
            </button>
          </div>
        ) : !wedding ? (
          <div className="animate-pulse font-wed text-xl py-24 text-center" style={{ color: "var(--wed-primary)" }}>Caricamento…</div>
        ) : (
          <ActiveTab key={activeId} wedding={wedding} setWedding={setWedding} />
        )}
      </main>

      {showNew && (
        <NewWeddingModal
          onClose={() => setShowNew(false)}
          onCreated={(wed) => { setShowNew(false); loadWeddings(wed.id); }}
        />
      )}
    </div>
  );
}
